import { StateCreator } from 'zustand'
import { ClockinHistory, NewClockin } from '../_common/interfaces'
import { mercuryApi } from '$api'
import { logger, enqueueLoadedSnackbar, exceptionMessageHandler } from '$utils'
import dayjs, { Dayjs } from 'dayjs'
import { ClockinDetail, ReportsMetadata } from '$models'
import { SharedSlice } from 'src/components/slice'
import { TimeclockSharedSlice } from '../slice'

interface StaffClocksState {
  showDeactivated: boolean
}

export interface StaffClocksSlice extends StaffClocksState {
  setShowDeactivatedClocks: () => void

  /**
   * Resets StaffClocks Slice state
   */
  resetStaffClocksSlice: () => void

  /**
   * Get ClockinDetail history for the clockinId provided
   * @param clockinId ClockinDetail identifier
   * @returns Promise<ClockinHistory[]>
   */
  getClockinDetailHistory: (clockinId: string) => Promise<ClockinHistory[]>

  /**
   * Add new ClockinDetail
   * @param newClockin ClockinDetail to add
   * @param reportsMetadata Reports metadata (date and date range filters)
   * @param successMessage Success Message to display if the update was successful
   * @param errorMessage Error Message to display if the update has failed
   */
  addClockinDetail: (
    newClock: NewClockin,
    reportsMetadata: ReportsMetadata,
    successMessage?: string,
    errorMessage?: string
  ) => void
}

const initialState: StaffClocksState = {
  showDeactivated: false,
}

const createStaffClocksSlice: StateCreator<
  StaffClocksSlice & TimeclockSharedSlice & SharedSlice,
  [],
  [],
  StaffClocksSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  setShowDeactivatedClocks: () =>
    set((state) => ({ showDeactivated: !state.showDeactivated })),
  resetStaffClocksSlice: () => set(initialState),

  // State API calls
  addClockinDetail: async (
    newClockin: NewClockin,
    reportsMetadata: ReportsMetadata,
    successMessage?: string,
    errorMessage?: string
  ) => {
    try {
      set(() => ({ isLoading: true }))

      // TAKEN FROM ANGULAR:
      // Small hack for getting around the problem where changing the hourlyRate updates
      // the newClock's hourlyRate from undefined to null.
      // the latter will trigger a validation error
      // See LOADED-1226
      if (newClockin.hourlyRate == null) {
        delete newClockin.hourlyRate
      }

      const newClockinDetail: ClockinDetail = await mercuryApi.add(
        '/time-clockins',
        newClockin
      )
      if (!newClockinDetail) {
        logger.error(`${errorMessage} - newClockinDetail doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      /* If the clockin time is not between the current date range then it is not added to the current clockins list
    but the clockin has been added successfully and that is why the snackbar message */
      const clockinTime: Dayjs = dayjs(newClockinDetail.clockinTime)
      if (
        clockinTime.isBetween(
          reportsMetadata.startDateTime,
          reportsMetadata.endDateTime,
          'day',
          '[]'
        )
      ) {
        set((state) => ({
          allClockins: [...state.allClockins, newClockinDetail],
        }))

        enqueueLoadedSnackbar(successMessage, 'success')
        return
      }

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    } finally {
      set(() => ({ isLoading: false }))
    }
  },

  getClockinDetailHistory: async (
    clockinId: string
  ): Promise<ClockinHistory[]> => {
    const filter = `$filter=ClockinId eq ${clockinId}`
    return mercuryApi
      .get<ClockinHistory[]>(`/time/clockins/histories?${filter}`)
      .catch((error) => {
        exceptionMessageHandler(error, 'Failed to retrieve clickin history')
        return []
      })
  },
})

export default createStaffClocksSlice
